import * as React from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";

import { TextField, Tooltip } from "@mui/material";
import { useField } from "formik";
import _ from "lodash";
import styled from "@emotion/styled";
import { useYupContext } from "../../../context/RequiredFields.context";

export default function DatePickerField(props: IDatePickerFieldProps) {
  const { initialTouched = false } = props;
  const id = window.crypto.randomUUID();
  const [field, meta, helpers] = useField({ name: props.name });
  const [value, setValue] = React.useState<any>(
    dayjs(field.value) ?? dayjs(new Date())
  );
  const [Focus, setFocus] = React.useState(false);
  const { requiredFields } = useYupContext();

  const setChange = _.debounce((newValue: any) => {
    helpers.setValue(dayjs(newValue).format("YYYY-MM-DD"));
  }, 500);

  const handleChange = (newValue: any) => {
    setValue(newValue);
    setChange(newValue);
    props.onChange && props.onChange(newValue);
  };

  const helperText = React.useMemo(() => {
    const errorText = meta.touched ? meta?.error : props.customError || "";
    return <Tooltip title={errorText}>{errorText}</Tooltip>;
  }, [meta.touched, meta.error, props.customError]);

  const handleFocus = React.useCallback(() => {
    setFocus(true);
    helpers.setTouched(true);
  }, [helpers]);

  const handleBlur = React.useCallback(() => {
    setFocus(false);
    helpers.setTouched(true);
  }, [helpers]);

  React.useEffect(() => {
    setValue(dayjs(field.value) ?? dayjs(new Date()));
  }, [field.value]);

  React.useEffect(() => {
    if (props.initialTouched) {
      helpers.setTouched(true, true);
    }
  }, [props.initialTouched]);

  return (
    <DatePicker
      value={value}
      onChange={handleChange}
      OpenPickerButtonProps={{
        id: id
      }}
      disabled={props.disabled}
      renderInput={params => (
        <Input
          fullWidth={true}
          {...params}
          name={props.name}
          size="small"
          focused={Focus}
          helperText={helperText}
          error={meta.touched && meta.error ? true : false}
          onFocus={handleFocus}
          onBlur={handleBlur}
          label={props.label}
          InputLabelProps={{ shrink: Focus || value?.isValid() }}
          onClick={() => {
            document.getElementById(id)?.click();
          }}
          className={`${
            requiredFields[props.name!] || props.isRequired ? "required" : ""
          }`}
        />
      )}
      disableFuture={props.disableFuture}
    />
  );
}

interface IDatePickerFieldProps {
  name: string;
  label: string;
  value?: string;
  customError?: string | any;
  isRequired?: boolean;
  onChange?: (value: string) => void;
  disableFuture?: boolean;
  initialTouched?: boolean;
  disabled?: boolean;
}

const Input = styled(TextField)`
  .MuiFormLabel-root {
    font-size: var(--fs-text-small);
    color: var(--m-color-blue-03);
    border-color: #ff0000 !important;
    &.Mui-focused {
      color: var(--color-blue-main);
    }
  }

  .input-with-icon {
    .MuiFormLabel-root {
      font-size: var(--fs-text-small);
      color: var(--m-color-blue-03);
      margin-left: 8px;
      &.Mui-focused {
        color: var(--color-blue-main);
      }
    }
  }

  input.MuiInputBase-input.MuiOutlinedInput-input {
    height: var(--height-input);
  }

  & .MuiFormHelperText-root {
    color: var(--m-color-red-01) !important;
    opacity: 0.8;
    font-size: 11px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .MuiOutlinedInput-root {
    border-radius: 20px;
    font-size: var(--fs-text-small);
  }
`;
